import { HeatMapSerie } from '@nivo/heatmap';
import classNames from 'classnames';
import { addDays, addHours, format, getWeek, startOfWeek } from 'date-fns';
import { useAtomValue } from 'jotai';
import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import naturalCompare from 'string-natural-compare';
import { TwTheme } from '../../../Tw';
import {
  getHeatmapLinearRangeBucketByValue,
  HEATMAP_BUCKETS_COUNT,
} from '../../common/heatmap.helper';
import useGetNamedColor from '../../common/useGetNamedColor';
import { commonMarginConfig } from '../../common/utils/chartConfig';
import InboxZero from '../../components/InboxZero';
import { Container } from '../../components/layout/ContainerFlex';
import ActualityAggregatedStatsBarChart from '../charts/ActualityAggregatedStatsBarChart';
import ActualityAggregatedStatsHeatmapChart, {
  ActualityAggregatedStatsTimelineHeatmapItem,
} from '../charts/ActualityAggregatedStatsHeatmapChart';
import ChartErrorBoundary from '../charts/ChartErrorBoundary';
import {
  calculateDataSize,
  ChartPerformanceMonitor,
  sampleData,
  validateChartData,
} from '../charts/chartDataValidator';
import ChartHeader from '../components/ChartHeader';
import ChartSidebar from '../components/ChartSidebar';
import FeedDashboardPanel from '../components/FeedDashboardPanel';
import useGetActualityAggregationMetricValue from '../hooks/useGetActualityAggregationMetricValue';
import ActualityKpiMetricsTable from '../kpiMetricsTable/ActualityKpiMetricsTable';
import { getAggregatedStatsKey } from '../store/actuality.kpi.helper';
import {
  actualityAggregatedStatsBuckets,
  actualityAggregatedStatsByGroupAndTime,
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsGroupBySecondary,
  actualityAggregatedStatsGroupIdsSorted,
  actualityAggregatedStatsMaxMetricValue,
  actualityAggregatedStatsMinMetricValue,
  actualityAggregatedStatsSelectedAggregatedMetric,
  actualityAggregatedStatsSelectedGroups,
} from '../store/actuality.kpi.state';
import {
  actualityAggregatedStatsDataPageSelected,
  actualityAggregatedStatsViewModeSelected,
} from '../store/actuality.kpi.ui.state';
import { SWARM_PAGE_SIZE } from '../store/feed.default';
import ActualityAggregatedStatsPagination from './ActualityAggregatedStatsPagination';

const colors = TwTheme.extend.colors;
const theme = TwTheme.extend.colors.chart;

function getLevelsConfig(granularity: string) {
  if (granularity === 'eventHour') {
    // 3 levels: week+month, day, hour
    return [
      {
        type: 'custom',
        className: 'bg-menu/50',
        labelAlign: 'start',
        groupBy: (tick: any) => {
          if (tick instanceof Date && !isNaN(tick.getTime())) {
            const weekStart = startOfWeek(tick, { weekStartsOn: 1 });
            return `${format(weekStart, 'yyyy')}-W${getWeek(tick)}`;
          }
          return String(tick);
        },
        formatter: (tick: any) => {
          if (tick instanceof Date && !isNaN(tick.getTime())) {
            const weekNum = getWeek(tick);
            const monthName = format(tick, 'MMM');
            return `${monthName} W${weekNum}`;
          }
          return String(tick);
        },
        spanCalculator: (tick: any, allTicks: any[]) => {
          if (!(tick instanceof Date) || isNaN(tick.getTime())) {
            return allTicks.filter(t => t === tick).length;
          }
          const weekStart = startOfWeek(tick, { weekStartsOn: 1 });
          const weekKey = `${format(weekStart, 'yyyy')}-W${getWeek(tick)}`;
          return allTicks.filter(t => {
            if (!(t instanceof Date) || isNaN(t.getTime())) return false;
            const tWeekStart = startOfWeek(t, { weekStartsOn: 1 });
            return `${format(tWeekStart, 'yyyy')}-W${getWeek(t)}` === weekKey;
          }).length;
        },
      },
      {
        type: 'day',
        style: { fontWeight: 'bold' },
        labelAlign: 'start',
        className: 'bg-menu/30 sticky left-0 shadow-2xl',
        stickyText: true,
      },
      {
        type: 'hour',
        style: { fontWeight: 'bold' },
        labelAlign: 'center',
        formatter: (tick: Date) => format(tick, 'HH:mm'),
        className: 'bg-menu/10 shadow-2xl',
        hasIssue: true,
      },
    ];
  }
  if (granularity === 'eventDay') {
    // 2 levels: week+month, day
    return [
      {
        type: 'custom',
        className: 'bg-menu/50',
        labelAlign: 'start',
        groupBy: (tick: any) => {
          if (tick instanceof Date && !isNaN(tick.getTime())) {
            const weekStart = startOfWeek(tick, { weekStartsOn: 1 });
            return `${format(weekStart, 'yyyy')}-W${getWeek(tick)}`;
          }
          return String(tick);
        },
        formatter: (tick: any) => {
          if (tick instanceof Date && !isNaN(tick.getTime())) {
            const weekNum = getWeek(tick);
            const monthName = format(tick, 'MMM');
            return `${monthName} W${weekNum}`;
          }
          return String(tick);
        },
        spanCalculator: (tick: any, allTicks: any[]) => {
          if (!(tick instanceof Date) || isNaN(tick.getTime())) {
            return allTicks.filter(t => t === tick).length;
          }
          const weekStart = startOfWeek(tick, { weekStartsOn: 1 });
          const weekKey = `${format(weekStart, 'yyyy')}-W${getWeek(tick)}`;
          return allTicks.filter(t => {
            if (!(t instanceof Date) || isNaN(t.getTime())) return false;
            const tWeekStart = startOfWeek(t, { weekStartsOn: 1 });
            return `${format(tWeekStart, 'yyyy')}-W${getWeek(t)}` === weekKey;
          }).length;
        },
      },
      {
        type: 'day',
        style: { fontWeight: 'bold' },
        labelAlign: 'start',
        className: 'bg-menu/30 sticky left-0 shadow-2xl',
        stickyText: true,
      },
    ];
  }
  // For all other granularities, just 1 level (e.g., day, week, month, etc.)
  return [
    {
      type: 'day',
      style: { fontWeight: 'bold' },
      labelAlign: 'start',
      className: 'bg-menu/30 sticky left-0 shadow-2xl',
      stickyText: true,
    },
  ];
}

const ActualityAggregatedStatsTimeline: React.FC = () => {
  const { t } = useTranslation('feed');
  const [forceRender, setForceRender] = useState(false);
  const [dataReduced, setDataReduced] = useState(false);

  // Performance monitor
  const performanceMonitor = useMemo(() => new ChartPerformanceMonitor(), []);

  const aggregatedStats = useRecoilValue(
    actualityAggregatedStatsByGroupAndTime,
  );
  const sortBy = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );
  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const groupBySecondary = useRecoilValue(
    actualityAggregatedStatsGroupBySecondary,
  );
  const selectedGroups = useRecoilValue(actualityAggregatedStatsSelectedGroups);

  const sortedGroupIds = useRecoilValue(actualityAggregatedStatsGroupIdsSorted);
  const selectedPage = useAtomValue(actualityAggregatedStatsDataPageSelected);

  const minValue = useRecoilValue(actualityAggregatedStatsMinMetricValue);
  const maxValue = useRecoilValue(actualityAggregatedStatsMaxMetricValue);
  const buckets = useRecoilValue(actualityAggregatedStatsBuckets);
  const viewMode = useAtomValue(actualityAggregatedStatsViewModeSelected);
  const getMetricValue = useGetActualityAggregationMetricValue();

  const getNamedColor = useGetNamedColor();

  const getMetricColor = (value: number) => {
    const bucket = getHeatmapLinearRangeBucketByValue({
      value,
      minValue,
      maxValue,
      bucketCount: HEATMAP_BUCKETS_COUNT,
      buckets: _.keyBy(buckets, 'id'),
    });
    return bucket?.color;
  };
  const showAll = _.isEmpty(selectedGroups);

  const effectiveGroupIds = _.filter(
    sortedGroupIds,
    groupValue =>
      showAll || _.some(selectedGroups, key => key[groupBy] === groupValue),
  );

  const groupIdsVisible =
    showAll && selectedPage !== -1
      ? _.slice(
          effectiveGroupIds,
          (selectedPage - 1) * SWARM_PAGE_SIZE,
          selectedPage * SWARM_PAGE_SIZE,
        )
      : effectiveGroupIds;

  const groupIdsVisibleSet = new Set(groupIdsVisible);

  const aggregatedStatsVisible = aggregatedStats;

  // Process data with validation and potential sampling
  const { chartData, validation } = useMemo(() => {
    performanceMonitor.start(aggregatedStatsVisible.length);

    let extendedEvents: ActualityAggregatedStatsTimelineHeatmapItem[] = _(
      aggregatedStatsVisible,
    )
      .filter(e => groupIdsVisibleSet.has(e[groupBy]))
      .map(stats => {
        const groupValue = stats[groupBy] as string;
        const metricValue = stats[sortBy] as any as number;
        const item: ActualityAggregatedStatsTimelineHeatmapItem = {
          ...stats,
          _color: getMetricColor(metricValue),
          _groupColor: _.head(getNamedColor(groupValue)),
          _key: getAggregatedStatsKey(stats, [groupBy, groupBySecondary]),
          x: stats[groupBySecondary] as any,
          y: metricValue,
        };
        return item;
      })
      .flatten()
      .value();

    // Validate data size before processing
    const validation = validateChartData(extendedEvents, 'heatmap', {
      // Custom limits for this specific chart
      optimal: 2000,
      warning: 8000,
      maximum: 25000,
    });

    // Sample data if it's too large and user chose to reduce
    if (dataReduced && validation.level === 'critical') {
      const targetSize = 5000; // Reduce to manageable size
      extendedEvents = sampleData(extendedEvents, targetSize, 'systematic');
    }

    const ticksWithValues = _(extendedEvents)
      .map(e => e[groupBySecondary])
      .uniqWith(_.isEqual)
      .value()
      .sort((a, b) =>
        naturalCompare(a?.toString() ?? 'ZZZZZ', b?.toString() ?? 'ZZZZZ', {
          caseInsensitive: true,
        }),
      );

    const ticksWithValuesSet = new Set(_.map(ticksWithValues, _.toString));

    let visibleTicks = ticksWithValues;
    if (groupBySecondary === 'eventHour') {
      visibleTicks = _(ticksWithValues)
        .flatMap((date: Date) => [date, addHours(date, 1)])
        .uniqWith(_.isEqual)
        .orderBy(d => d.getTime(), 'asc')
        .initial()
        .value();
    }
    if (groupBySecondary === 'eventDay') {
      visibleTicks = _(ticksWithValues)
        .flatMap((date: Date) => [date, addDays(date, 1)])
        .uniqWith(_.isEqual)
        .orderBy(d => d.getTime(), 'asc')
        .initial()
        .value();
    }

    function createEmptyHeatmapItem(
      groupByValue: any,
      tick: any,
    ): ActualityAggregatedStatsTimelineHeatmapItem {
      const isGap = !ticksWithValuesSet.has(_.toString(tick));

      return {
        [groupBy]: groupByValue,
        [groupBySecondary]: tick,
        _color: isGap ? theme.transparent : theme.empty,
        _groupColor: isGap ? theme.transparent : theme.empty,
        x: tick,
        y: 0,
        _type: isGap ? 'gap' : 'placeholder',
      } as any;
    }

    const chartData: HeatMapSerie<
      ActualityAggregatedStatsTimelineHeatmapItem,
      {}
    >[] = _(extendedEvents)
      .groupBy(e => e[groupBy])
      .map((data, id) => {
        const dataSet = _.keyBy(data, groupBySecondary);
        const extendedData = _(visibleTicks)
          .map(
            t =>
              dataSet[t?.toString() ?? 'NULL'] ?? createEmptyHeatmapItem(id, t),
          )
          .orderBy('x')
          .value();

        return {
          id,
          data: _.take(extendedData, 50),
        };
      })
      .orderBy(({ id }) => _.indexOf(sortedGroupIds, id), 'asc')
      .value();

    return { chartData, validation };
  }, [
    aggregatedStatsVisible,
    groupIdsVisibleSet,
    groupBy,
    sortBy,
    groupBySecondary,
    getMetricColor,
    getNamedColor,
    sortedGroupIds,
    dataReduced,
    performanceMonitor,
  ]);

  const hourWidth = 120;

  const chartContainerWidth =
    _.size(chartData[0]?.data || []) > 12
      ? `${hourWidth * _.size(chartData[0]?.data || [])}px`
      : '100%';
  const shouldShowSummaryChart = viewMode === 'chart' || viewMode === 'compare';

  const shouldShowDetailedHeatmapChart =
    viewMode === 'heatmap' || viewMode === 'compare';

  const shouldShowDashboard = viewMode === 'dashboard';

  const shouldShowTable = viewMode === 'table';

  const shouldShowChartHeader =
    shouldShowDetailedHeatmapChart || shouldShowSummaryChart;

  const hasData = _.size(chartData) > 0;

  // Handle blocked datasets
  if (validation.level === 'blocked' && !forceRender) {
    return (
      <div className="flex h-full flex-col p-4">
        {/* <ChartDataWarning
          validation={validation}
          onProceed={() => setForceRender(true)}
          onReduce={() => setDataReduced(true)}
          canReduce={true}
          className="mb-4"
        /> */}
        <InboxZero
          selfCenter
          message={validation.message || t`Dataset too large`}
        />
      </div>
    );
  }

  if (!hasData) {
    return <InboxZero selfCenter message={t`No Data`} />;
  }

  const safeVisibleTicks = (chartData[0]?.data || []).map(item => {
    const tick = item.x;
    if (typeof tick === 'string' || typeof tick === 'number') {
      const d = new Date(tick);
      return !isNaN(d.getTime()) ? d : tick;
    }
    return tick;
  });

  const isAllSelected = selectedPage === -1;

  const numberOfRows = chartData.length;
  const baseCellHeight = viewMode === 'heatmap' ? 38 : 23; // Base height per row
  // const baseCellHeight = viewMode === 'heatmap' ? 38 : 23; // Base height per row
  const calculatedHeight = numberOfRows * baseCellHeight;

  // Calculate dynamic cell height based on number of rows when "ALL" is selected
  // const calculateCellHeight = () => {
  //   if (!isAllSelected) {
  //     return undefined; // Auto-fit when not "ALL"
  //   }

  //   const numberOfRows = chartData.length;
  //   const baseCellHeight = 20; // Base height per row
  //   const calculatedHeight = numberOfRows * baseCellHeight;

  //   // Set reasonable min/max bounds
  //   const minHeight = 200; // Minimum total height
  //   // const maxHeight = 800; // Maximum total height

  //   if (calculatedHeight < minHeight) {
  //     return minHeight / numberOfRows;
  //   }
  //   // if (calculatedHeight > maxHeight) {
  //   //   return maxHeight / numberOfRows;
  //   // }

  //   return baseCellHeight;
  // };

  // const dynamicCellHeight = calculateCellHeight();

  /**
   * Height Calculation Logic:
   *
   * 1. Individual Mode (not all selected):
   *    - Auto-fit to 100% of container height
   *    - No scroll, rows stretch to fit
   *
   * 2. Individual Mode (all selected):
   *    - Fixed height per row (38px for heatmap)
   *    - Vertical scroll if content exceeds container
   *
   * 3. Compare Mode (not all selected):
   *    - Bar chart: 40% height
   *    - Heatmap: 60% height
   *    - No scroll, rows stretch to fit
   *
   * 4. Compare Mode (all selected):
   *    - Bar chart: 40% height
   *    - Heatmap: 60% height with vertical scroll
   *    - Fixed height per row (23px for compare)
   */
  const calculateHeights = () => {
    const isCompareMode = viewMode === 'compare';
    const isHeatmapMode = viewMode === 'heatmap';

    // Base cell heights for different modes
    const BASE_CELL_HEIGHT_HEATMAP = 38;
    const BASE_CELL_HEIGHT_COMPARE = 23;
    const BASE_CELL_HEIGHT_DEFAULT = 30;

    // Get the base height based on mode
    const baseCellHeight = isHeatmapMode
      ? BASE_CELL_HEIGHT_HEATMAP
      : isCompareMode
        ? BASE_CELL_HEIGHT_COMPARE
        : BASE_CELL_HEIGHT_DEFAULT;

    // For individual rows mode (not all selected)
    if (!isAllSelected) {
      // Auto-fit to container
      return {
        cellHeight: undefined, // Let Nivo auto-calculate
        rowHeight: `${baseCellHeight}px`,
        containerHeight: undefined, // Use 100% of available space
        enableScroll: false,
      };
    }

    // For "All" selected mode
    const totalHeight = numberOfRows * baseCellHeight;

    // In compare mode with "All" selected
    if (isCompareMode) {
      return {
        cellHeight: totalHeight, // Total height for all rows
        rowHeight: `${baseCellHeight}px`,
        containerHeight: '60%', // 60% for heatmap (since bar chart takes 40%)
        enableScroll: true, // Enable vertical scroll
      };
    }

    // In heatmap/other modes with "All" selected
    return {
      cellHeight: totalHeight, // Total height for all rows
      rowHeight: `${baseCellHeight}px`,
      containerHeight: '100%', // Full height with scroll
      enableScroll: true, // Enable vertical scroll
    };
  };

  const { cellHeight, rowHeight, containerHeight, enableScroll } =
    calculateHeights();

  return (
    <Container
      data-component="ActualityAggregated"
      col
      hasOverflowX
      // overflow={viewMode === 'heatmap' && isAllSelected ? true : false}
      className="relative"
    >
      <ChartErrorBoundary
        dataSize={calculateDataSize(chartData)}
        maxDataSize={25000}
        onError={error => {
          console.error('Chart render failed:', error);
          performanceMonitor.end('heatmap-timeline');
        }}
      >
        {/* Show warning for large datasets */}
        {/* <ChartDataWarning
          validation={validation}
          onProceed={() => setForceRender(true)}
          onReduce={() => setDataReduced(true)}
          canReduce={true}
          className="mb-2"
        /> */}

        <div
          data-component={`ActualityAggregated-${viewMode}-${groupBy}-${groupBySecondary}`}
          className={classNames(
            'relative flex',
            shouldShowDashboard ? 'h-full w-full' : 'flex-col',
          )}
          style={{
            width: shouldShowDashboard ? null : chartContainerWidth,
            height: '100%',
          }}
        >
          {shouldShowTable && (
            <ActualityKpiMetricsTable id="actuality-kpi-metrics-timeline-table" />
          )}
          {shouldShowDashboard && <FeedDashboardPanel />}

          {shouldShowSummaryChart && (
            <ChartErrorBoundary
              dataSize={calculateDataSize(_.flatMap(chartData, i => i.data))}
              maxDataSize={25000}
            >
              <ActualityAggregatedStatsBarChart
                data={_.flatMap(chartData, i => i.data)}
                fullHeight={isAllSelected}
                compareMode={viewMode === 'compare'}
                showAxisX={false}
                showAxisY={false}
              />
            </ChartErrorBoundary>
          )}

          {shouldShowChartHeader && (
            <>
              <ChartHeader
                ticks={safeVisibleTicks as Date[]}
                position="bottom"
                sticky={true}
                absolute={false}
                showShiftLayers={true}
                showWeekendHighlight={true}
                granularity={groupBySecondary as any}
                leftOffset={`${commonMarginConfig.left}px`}
                rightOffset={`${commonMarginConfig.right}px`}
                skipEmptyLevels={false}
                levelsConfig={getLevelsConfig(groupBySecondary) as any}
              />

              <div className="absolute left-0 top-0 z-20 w-[150px] p-2 pl-4">
                <ActualityAggregatedStatsPagination />
              </div>
            </>
          )}

          {shouldShowDetailedHeatmapChart && (
            <div
              data-component="ActualityAggregated-Heatmap"
              className={classNames(
                'relative flex flex-1',
                viewMode === 'compare' && 'h-full',
                viewMode === 'heatmap' && 'h-full',
                isAllSelected ? 'flex-row overflow-x-auto' : 'flex-col',
              )}
            >
              <div
                className={classNames(
                  '',
                  isAllSelected
                    ? 'sticky left-0 top-0 z-20'
                    : 'absolute bottom-0 left-0 right-0 top-0',
                )}
              >
                <ChartSidebar
                  rows={_.map(
                    chartData,
                    (
                      t: HeatMapSerie<
                        ActualityAggregatedStatsTimelineHeatmapItem,
                        {}
                      >,
                    ) => ({
                      id: t.id,
                      label: t.id,
                      color: [theme.empty, theme.empty] as [string, string],
                      isSelected: false,
                      onClick: () => {},
                      onHover: () => {},
                    }),
                  )}
                  position="left"
                  sticky={isAllSelected ? false : true}
                  width={`${commonMarginConfig.left}px`}
                  showHeader={false}
                  topOffset="0px"
                  className={classNames(
                    'shadow-2xl',
                    `w-[150px]`,
                    `pt-0`,
                    `pb-[48px]`,
                  )}
                  rowsHeight={
                    viewMode === 'heatmap' && isAllSelected
                      ? `${baseCellHeight}px`
                      : viewMode === 'compare'
                        ? null
                        : `${baseCellHeight}px`
                  }
                />
              </div>

              <ChartErrorBoundary
                dataSize={calculateDataSize(chartData)}
                maxDataSize={25000}
              >
                <ActualityAggregatedStatsHeatmapChart
                  chartData={chartData}
                  xAxisField={groupBySecondary}
                  yAxisField={groupBy}
                  ticksValues={new Set(_.map(safeVisibleTicks, _.toString))}
                  fullHeight={isAllSelected}
                  compareMode={viewMode === 'compare'}
                  cellHeight={
                    viewMode === 'heatmap' && isAllSelected
                      ? calculatedHeight
                      : calculatedHeight
                  }
                />
              </ChartErrorBoundary>
            </div>
          )}
        </div>
      </ChartErrorBoundary>
    </Container>
  );
};

export default ActualityAggregatedStatsTimeline;
